import React from "react";
import {
  RefreshCw,
  Package,
  Clock,
  AlertTriangle,
  CheckCircle,
  Mail,
  Phone,
} from "lucide-react";
import Card from "../modern-components/ui/Card";
import { Link } from "react-router-dom";

const steps = [
  {
    step: "01",
    title: "Raise a Request",
    desc: "Contact our support team within 48 hours of delivery to initiate a return.",
  },
  {
    step: "02",
    title: "Inspection",
    desc: "Our team reviews your request and coordinates inspection of the goods.",
  },
  {
    step: "03",
    title: "Pickup Arranged",
    desc: "We arrange reverse logistics to collect the goods from your location.",
  },
  {
    step: "04",
    title: "Refund Processed",
    desc: "Upon receipt and verification, refunds are processed within 7–10 business days.",
  },
];

const eligibleReasons = [
  "Wrong product delivered (different from order specifications)",
  "Damaged goods received — damage must be reported within 48 hours",
  "Significant quality defects verified by our team",
  "Short shipment (quantity less than invoiced)",
];

const notEligibleReasons = [
  "Change of mind after delivery",
  "Products cut, processed, or altered after delivery",
  "Damage caused by improper handling or storage at your end",
  "Returns requested after the 48-hour reporting window (unless quality dispute)",
  "Custom-manufactured or made-to-order products",
];

const ReturnRefund = () => {
  return (
    <div className="min-h-screen bg-white pt-40 pb-16">
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary-600 to-secondary-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full border border-white/20 mb-6">
            <RefreshCw className="w-4 h-4" />
            <span className="text-sm font-semibold tracking-wide">POLICY</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Return & Refund Policy
          </h1>
          <p className="text-lg text-gray-100">
            We stand behind the quality of our products. Here's how we handle
            returns.
          </p>
          <p className="text-sm text-gray-300 mt-3">
            Last updated: February 2025
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        {/* Important Note */}
        <Card className="mb-10 border-l-4 border-orange-400 bg-orange-50">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" />
            <p className="text-gray-700 leading-relaxed">
              <strong>Important:</strong> All return requests must be raised
              within <strong>48 hours</strong> of delivery. Claims for damaged
              goods require photographic evidence submitted at the time of
              reporting. lohamart deals in B2B industrial materials — all
              returns are subject to inspection and approval.
            </p>
          </div>
        </Card>

        {/* Return Process */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Return Process
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s) => (
              <Card
                key={s.step}
                className="text-center border border-gray-100 hover:border-primary-300 transition-colors"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-primary-600 to-secondary-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-lg">{s.step}</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {s.desc}
                </p>
              </Card>
            ))}
          </div>
        </div>

        {/* Eligible / Not Eligible */}
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          <Card className="border border-green-100 bg-green-50">
            <div className="flex items-center space-x-2 mb-4">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <h2 className="text-xl font-bold text-gray-900">
                Eligible for Return
              </h2>
            </div>
            <ul className="space-y-3">
              {eligibleReasons.map((r, i) => (
                <li key={i} className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0 mt-2" />
                  <span className="text-gray-700 text-sm leading-relaxed">
                    {r}
                  </span>
                </li>
              ))}
            </ul>
          </Card>

          <Card className="border border-red-100 bg-red-50">
            <div className="flex items-center space-x-2 mb-4">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              <h2 className="text-xl font-bold text-gray-900">
                Not Eligible for Return
              </h2>
            </div>
            <ul className="space-y-3">
              {notEligibleReasons.map((r, i) => (
                <li key={i} className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0 mt-2" />
                  <span className="text-gray-700 text-sm leading-relaxed">
                    {r}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </div>

        {/* Refund Details */}
        <Card className="mb-8 border border-gray-100">
          <div className="flex items-start space-x-3 mb-4">
            <Clock className="w-6 h-6 text-primary-600 flex-shrink-0" />
            <h2 className="text-xl font-bold text-gray-900">Refund Timeline</h2>
          </div>
          <div className="space-y-4 text-gray-700">
            <p>
              Once the returned goods are received and verified by our quality
              team, refunds are initiated within{" "}
              <strong>7–10 business days</strong>.
            </p>
            <p>
              Refunds are credited back to the original payment method — bank
              account for bank transfers, or source account for UPI/online
              payments.
            </p>
            <p>
              For partial shipments or partial defects, a partial refund or
              credit note may be issued based on inspection results.
            </p>
          </div>
        </Card>

        {/* CTA */}
        <Card className="bg-gradient-to-br from-primary-600 to-secondary-600 text-white border-0">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="text-xl font-bold mb-2">
                Need to Raise a Return Request?
              </h2>
              <p className="text-gray-200 text-sm">
                Contact our support team immediately — remember the 48-hour
                window.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-lg">
                <Mail className="w-4 h-4 text-yellow-300" />
                <span className="text-sm">info@lohamart.in</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-lg">
                <Phone className="w-4 h-4 text-yellow-300" />
                <span className="text-sm">+91 94370 45109</span>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ReturnRefund;
